import { Sheet, SheetTrigger, Typography } from '@sunwebgroup/ui-components';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  Subtitle,
  TableCellWithSubtitle,
  TableBody,
  EmptyTableMessage,
} from './Table';
import { useGroupResponsibleStore } from '@/scopes/account/group-responsible/infrastructure/useGroupResponsibleStore';
import { useIntl } from '@/intl';
import { useGroupResponsibleDependencies } from '../dependencies';
import { useStore } from 'zustand';
import { identity } from 'ramda';
import { RoomExtraInfoSheetPortal } from './RoomExtraInfoSheetPortal';
import { RoomFilterValues } from '@/scopes/account/group-responsible/domain/types';
import type { RoomFilterType, GroupResponsibleStore } from '@/scopes/account/group-responsible/domain/types';
import { FilterToggleGroup } from './FilterToggleGroup';
import * as React from 'react';
import { getFilteredRoomsOccupied, getRoomsOccupiedCounts } from '../selectors';
import { TableSearch } from './TableSearch';

const breakpointHideCol = 'breakpoint_desktop_sm';

export function RoomsTable() {
  const [filter, setFilter] = React.useState<RoomFilterType>('all');
  const [searchTerm, setSearchTerm] = React.useState('');
  const counts = useGroupResponsibleStore(getRoomsOccupiedCounts);
  type CountKeys = keyof ReturnType<typeof getRoomsOccupiedCounts>;
  const filters: { value: RoomFilterType; label: string; countKey: CountKeys }[] = [
    { value: RoomFilterValues.ALL, label: 'groups_responsible_all', countKey: 'all' },
    { value: RoomFilterValues.NOT_OCCUPIED, label: 'groups_responsible_not_occupied', countKey: 'notOccupied' },
    { value: RoomFilterValues.OCCUPIED, label: 'groups_responsible_occupied', countKey: 'occupied' },
  ];
  const filteredRooms = useGroupResponsibleStore((state) => getFilteredRoomsOccupied(state, filter, searchTerm));
  const intl = useIntl();
  const { groupResponsibleStore } = useGroupResponsibleDependencies();
  const store = useStore(groupResponsibleStore, identity<GroupResponsibleStore>);

  function handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  return (
    <>
      <TableSearch searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      <FilterToggleGroup filter={filter} filters={filters} counts={counts} onFilterChange={setFilter} />
      {filteredRooms.length === 0 ? (
        <EmptyTableMessage />
      ) : (
        <Sheet modal>
          <Table>
            <thead>
              <TableRow>
                <TableHead>
                  <Typography $variant="body-sm">
                    {intl.formatMessage({ id: 'groups_responsible_table_room_name_label' })}
                  </Typography>
                </TableHead>
                <TableHead $hideOn={breakpointHideCol}>
                  <Typography $variant="body-sm">
                    {intl.formatMessage({ id: 'groups_responsible_room_type' })}
                  </Typography>
                </TableHead>
                <TableHead>
                  <Typography $variant="body-sm">
                    {intl.formatMessage({ id: 'groups_responsible_table_room_occupancy_label' })}
                  </Typography>
                </TableHead>
              </TableRow>
            </thead>
            <TableBody>
              {filteredRooms.map((room) => (
                <SheetTrigger
                  asChild
                  key={room.id}
                  onClick={() => {
                    store.changeExtraInfoId(room.id);
                  }}
                >
                  <TableRow>
                    <TableCellWithSubtitle>
                      <div>{room.name}</div>
                      <Subtitle $variant="body-sm">{room.participants.join(', ')}</Subtitle>
                    </TableCellWithSubtitle>
                    <TableCell $hideOn={breakpointHideCol}>{room.type ? room.type : '-'}</TableCell>
                    <TableCell>{room.occupancyText}</TableCell>
                  </TableRow>
                </SheetTrigger>
              ))}
            </TableBody>
          </Table>
          <RoomExtraInfoSheetPortal />
        </Sheet>
      )}
    </>
  );
}
